// Thin fetch wrapper around the FastAPI backend.

import type {
  ExperimentOutcome,
  HealthBook,
  HoldingsHealth,
  ExperimentPickIn,
  ExperimentReport,
  ExperimentSelection,
  ObsDays,
  ObsForwardGate,
  ObsScoreBands,
  ObsLearning,
  ObsOutcomes,
  ObsClaim,
  ObsRunDetail,
  ObsRuns,
  ObsStockDetail,
  ObsStocks,
  RealAccount,
  RealOrder,
  RealOrdersStatus,
  BrainAlertPreview,
  BrainAlertRecord,
  BrainAlertSend,
  BrainApproval,
  BrainCompare,
  BrainDecision,
  BrainEpisode,
  BrainLearning,
  BrainProposal,
  BrainStage,
  BrainStageName,
  BrainTrack,
  BrainWhatIf,
  BrainHealth,
  BrainModules,
  BrainRun,
  BrainRunQueued,
  MarketSession,
  BrainRunSummary,
  BrainTraceEvent,
  BrainWhy,
  Candle,
  CompanyView,
  CompareView,
  ShadowTestView,
  PriceSnapshot,
  CurrentUser,
  AdminUser,
  MyPlan,
  PlanCatalogue,
  RegistrySlot,
  RegistrySlotChange,
  RegistrySlots,
  LanePicks,
  LanePickRow,
  HorizonReport,
  LanesResponse,
  PlanConfig,
  PlanPick,
  TopPick,
  DetailedPosition,
  StockScoreTrail,
  EquityPoint,
  FinanceCalculationSummary,
  FinanceCategorySummary,
  FinanceCustomRule,
  FinanceFilters,
  FinanceIngestedFile,
  FinanceIngestResult,
  FinanceLoan,
  FinanceMerchantSummary,
  FinanceMonthlyCategorySummary,
  FinanceMonthlySummary,
  FinanceNetWorth,
  FinanceRecategorizeResult,
  FinanceRuleUpsertResult,
  FinanceTransaction,
  BuyListRow,
  FinanceDailyCategory,
  FinanceRecurringBill,
  Holding,
  Instrument,
  MyWatchlist,
  MyWatchlistItem,
  WatchlistHit,
  KiteLoginResponse,
  LatestSignal,
  HorizonAccuracy,
  MarketRegime,
  MLModel,
  CalibrationReport,
  CurrentLimits,
  MessageResponse,
  RiskEvent,
  SystemState,
  MutualFundHolding,
  MutualFundSearchResult,
  PortfolioSummary,
  Prediction,
  PredictionRun,
  RealReport,
  ScanResult,
  Strategy,
  StrategyPerformanceResponse,
  StrategySignal,
  StrategyType,
  SystemStatus,
  Trade,
  TrackRecordSignal,
} from './types'
import type { AlertSettings, AlertSettingsUpdate } from '../trademind/alerts'
import type { AdminJobs } from '../trademind/jobs'
import type { AuthOptions, InviteForm } from '../auth/authRules'
import type { InviteDecision, InviteRequestRow } from '../trademind/invites'
import type { BrainChanges } from '../trademind/changes'
import type { RealTaxExport } from '../trademind/taxRecords'

export const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000/api/v1'

// The dashboard authenticates as a real user: POST /auth/login or /auth/signup
// returns a JWT, which is what every subsequent request carries as a bearer
// token. (The backend also accepts a shared X-API-Key as an alternative, but
// that path exists for scripts/automation — the dashboard never uses it.)
const TOKEN_STORAGE = 'stml.token'

export function getToken(): string {
  return localStorage.getItem(TOKEN_STORAGE) ?? ''
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_STORAGE, token.trim())
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_STORAGE)
}

// The owner can view the app exactly as a Free / Plus / Pro user sees it.
// The chosen plan rides on every request as X-Preview-Plan; the backend only
// honours it for the owner. Storage can throw (private mode), so every
// access is guarded and "no preview" is the safe fallback.
const PREVIEW_STORAGE = 'stml.previewPlan'

export function getPreviewPlan(): string {
  try {
    return localStorage.getItem(PREVIEW_STORAGE) ?? ''
  } catch {
    return ''
  }
}

export function setPreviewPlan(plan: string | null): void {
  try {
    if (plan) localStorage.setItem(PREVIEW_STORAGE, plan)
    else localStorage.removeItem(PREVIEW_STORAGE)
  } catch {
    /* the preview just won't persist */
  }
}

function authHeaders(token: string): Record<string, string> {
  const preview = getPreviewPlan()
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(preview ? { 'X-Preview-Plan': preview } : {}),
  }
}

/**
 * Google sign-in comes back with `?login_code=...` (one use, 60 seconds).
 * Read it and strip it from the address bar before anything renders. An old
 * `?token=` link is stripped and never honoured.
 */
export function takeLoginCodeFromRedirect(): string | null {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('login_code') && !url.searchParams.has('token')) return null
  const code = url.searchParams.get('login_code')
  url.searchParams.delete('login_code')
  url.searchParams.delete('token')
  window.history.replaceState({}, '', url.toString())
  return code
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** `anonymous` sends no credentials at all, so a 401 can never clear a stored token. */
async function request<T>(path: string, init?: RequestInit, anonymous = false): Promise<T> {
  const token = anonymous ? '' : getToken()
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(anonymous ? {} : authHeaders(token)),
      ...init?.headers,
    },
  })

  if (!response.ok) {
    // A 401 on an authenticated request means the token expired or was
    // revoked — drop it so the app falls back to the login screen instead of
    // looping on the same failing request.
    if (response.status === 401 && token) {
      clearToken()
    }

    // FastAPI returns {detail: ...}; fall back to the status line when the
    // body is empty or not JSON (a proxy error page, for instance).
    let detail = response.statusText
    try {
      const body = await response.json()
      detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail)
    } catch {
      /* keep statusText */
    }
    throw new ApiError(detail, response.status)
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

/** Same as request<T>() but for multipart bodies (file uploads) — must NOT
 * set Content-Type itself, so the browser attaches the multipart boundary. */
async function requestForm<T>(path: string, form: FormData): Promise<T> {
  const token = getToken()
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    body: form,
    headers: authHeaders(token),
  })

  if (!response.ok) {
    if (response.status === 401 && token) {
      clearToken()
    }
    let detail = response.statusText
    try {
      const body = await response.json()
      detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail)
    } catch {
      /* keep statusText */
    }
    throw new ApiError(detail, response.status)
  }

  return response.json() as Promise<T>
}

/** A GET whose answer is a file (CSV), not JSON. Same credentials and errors as request<T>(). */
async function requestText(path: string): Promise<string> {
  const token = getToken()
  const response = await fetch(`${BASE_URL}${path}`, { headers: authHeaders(token) })
  if (!response.ok) {
    if (response.status === 401 && token) clearToken()
    let detail = response.statusText
    try {
      const body = await response.json()
      detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail)
    } catch {
      /* keep statusText */
    }
    throw new ApiError(detail, response.status)
  }
  return response.text()
}

/** Builds a `?a=1&b=2` suffix from a params object, dropping empty/undefined
 * values — shared by every filterable finance endpoint. Takes `object` rather
 * than `Record<string, ...>` so callers can pass a plain interface (e.g.
 * `FinanceFilters`) without hitting Record's index-signature requirement. */
function qs(params: object): string {
  const entries = Object.entries(params as Record<string, string | number | undefined>).filter(
    ([, v]) => v !== undefined && v !== '',
  ) as [string, string | number][]
  if (!entries.length) return ''
  const search = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]))
  return `?${search.toString()}`
}

const get = <T>(path: string) => request<T>(path)
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined })
/** A POST that carries no sign-in: for calls made before there is a session. */
const postPublic = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }, true)
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
const put = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) })
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' })

/** Full-page navigation, not a fetch: Google's consent screen must be a real
 * browser redirect, and the backend replies with one too. */
export function googleLoginUrl(): string {
  return `${BASE_URL}/auth/google/login`
}

export const api = {
  // ------------------------------------------------------------- system --
  status: () => get<SystemStatus>('/status'),
  refreshData: () => post<SystemStatus>('/refresh-data'),
  health: () => get<{ status: string; version: string }>('/health'),

  // -------------------------------------------------------------- auth --
  login: (username: string, password: string) =>
    post<{ access_token: string }>('/auth/login', { username, password }),
  exchangeLoginCode: (code: string) =>
    postPublic<{ access_token: string }>('/auth/google/exchange', { code }),
  changePassword: (current_password: string, new_password: string) =>
    post<{ access_token: string }>('/auth/password', { current_password, new_password }),
  logoutEverywhere: () => post<{ message: string }>('/auth/logout-all'),
  signup: (username: string, password: string, email?: string, inviteCode?: string) =>
    postPublic<{ access_token: string }>('/auth/signup', {
      username,
      password,
      email,
      ...(inviteCode ? { invite_code: inviteCode } : {}),
    }),
  authOptions: () => get<AuthOptions>('/auth/options'),
  requestInvite: (body: InviteForm) => {
    const phone = body.phone.trim()
    return postPublic<{ message: string }>('/auth/invite-request', { ...body, phone: phone || null })
  },
  me: () => get<CurrentUser>('/auth/me'),
  kiteLogin: () => get<KiteLoginResponse>('/auth/kite/login'),

  // -------------------------------------------------------------- plans --
  myPlan: () => get<MyPlan>('/me/plan'),
  planSwitch: () => get<{ enabled: boolean }>('/admin/switch'),
  setPlanSwitch: (enabled: boolean) => put<{ enabled: boolean }>('/admin/switch', { enabled }),
  planCatalogue: () => get<PlanCatalogue>('/admin/features'),
  // Model registry (phase 1, owner only): reads, plus one write that changes a slot's research notes.
  modelRegistrySlots: () => get<RegistrySlots>('/model-registry/slots'),
  modelRegistryChange: (slotKey: string, body: RegistrySlotChange) =>
    patch<RegistrySlot>(`/model-registry/slots/${encodeURIComponent(slotKey)}`, body),
  lanePicks: (horizon?: number, cap?: string) => {
    const q = new URLSearchParams()
    if (horizon) q.set('horizon', String(horizon))
    if (cap) q.set('cap', cap)
    const qs = q.toString()
    return get<LanePicks>(`/research/lane-picks${qs ? `?${qs}` : ''}`)
  },
  lanes: () => get<LanesResponse>('/research/lanes'),
  horizonReport: () => get<HorizonReport>('/research/lanes/horizon-report'),
  lanePickRows: (slotKey: string, limit = 100) =>
    get<LanePickRow[]>(`/research/lanes/${encodeURIComponent(slotKey)}/picks?limit=${limit}`),
  h05Report: () => get<{ runs: unknown[]; verdict?: { state?: string } }>('/research/h05/report'),
  plans: () => get<PlanConfig[]>('/admin/plans'),
  savePlan: (key: string, body: Pick<PlanConfig, 'features' | 'limits'>) =>
    put<PlanConfig>(`/admin/plans/${key}`, body),
  users: () => get<AdminUser[]>('/admin/users'),
  setUserPlan: (userId: number, plan: string) => put<AdminUser>(`/admin/users/${userId}/plan`, { plan }),

  // ---------------------------------------- scheduled jobs (owner, read-only) --
  adminJobs: () => get<AdminJobs>('/admin/jobs'),

  // ------------------------------------------- invites (signed-in owner only) --
  adminInvites: () => get<InviteRequestRow[]>('/admin/invites'),
  approveInvite: (id: number, note?: string) =>
    post<InviteDecision>(`/admin/invites/${id}/approve`, { note: note || null }),
  rejectInvite: (id: number, note?: string) =>
    post<InviteRequestRow>(`/admin/invites/${id}/reject`, { note: note || null }),
  newInviteCode: (id: number) => post<InviteDecision>(`/admin/invites/${id}/new-code`, {}),

  // ---------------------------------------------------------- portfolio --
  // Everything below asks for `book=bot`: the bot's own book, in whichever
  // mode the bot is running (paper rows while in paper, real ones once
  // live). It deliberately excludes the shares bought by hand in Zerodha —
  // those are the My Holdings book, and they are stored as mode="live"
  // whatever the bot is doing, so a mode filter alone would merge the two
  // the day we switch to live.
  summary: (mode?: string) =>
    get<PortfolioSummary>(`/portfolio/summary?book=bot${mode ? `&mode=${mode}` : ''}`),
  positions: () => get<DetailedPosition[]>('/portfolio/positions/detailed?book=bot'),
  // Both books at once, for the Strategies tab's per-strategy "held right
  // now" count — `real_trading` is a strategy in that list too, so counting
  // only the bot's book would report it as holding nothing once we're live.
  positionsAllBooks: () => get<DetailedPosition[]>('/portfolio/positions/detailed?book=all'),
  // The My Holdings book — shares bought by hand in Zerodha, tracked under
  // the advisory "real_trading" strategy with the same confidence/stop-loss
  // fields, but never traded by the bot.
  realPositions: () => get<DetailedPosition[]>('/portfolio/positions/detailed?book=real'),
  // Read-only health check of ONE book's holdings (never "all": paper and real are never added together).
  // book=real is refused by the server for anyone not in REAL_ACCOUNT_USERS.
  holdingsHealth: (book: HealthBook) => get<HoldingsHealth>(`/portfolio/health?book=${book}`),
  // strategyId is optional: the backend creates or reuses the advisory
  // `real_trading` strategy when it's omitted, so a fresh install doesn't
  // need one hand-built first.
  importRealHoldings: (strategyId?: number) =>
    post<
      {
        symbol: string
        status: string
        reason?: string
        quantity?: number
        entry_price?: number
        current_price?: number
        stop_loss?: number | null
        take_profit?: number | null
        levels_basis?: string
      }[]
    >(
      strategyId == null
        ? '/portfolio/positions/import-holdings'
        : `/portfolio/positions/import-holdings?strategy_id=${strategyId}`,
    ),
  holdings: () => get<Holding[]>('/portfolio/holdings'),
  closePosition: (id: number, reason = 'MANUAL') =>
    post<MessageResponse>(`/portfolio/positions/${id}/close`, { reason }),
  // Real positions have no broker order to auto-close against — this
  // records the sale you already made yourself in Zerodha, at the price you
  // actually got, rather than closePosition() above (which would place a
  // real/paper order through whichever broker the app is currently in).
  manualClosePosition: (id: number, exitPrice: number) =>
    post<Trade>(`/portfolio/positions/${id}/manual-close`, { exit_price: exitPrice }),
  // REAL orders to Zerodha for My Holdings — owner only, refused by the
  // server unless the real-orders switch is on and the limits allow it.
  // Whole Zerodha account at a glance — live, read-only.
  realAccount: () => get<RealAccount>('/portfolio/real/account'),
  realOrdersStatus: () => get<RealOrdersStatus>('/portfolio/real/orders-status'),
  realQuote: (symbol: string) =>
    get<{ symbol: string; exchange: string; price: number }>(
      `/portfolio/real/quote?symbol=${encodeURIComponent(symbol)}`,
    ),
  realBuy: (symbol: string, quantity: number) =>
    post<RealOrder>('/portfolio/real/buy', { symbol, quantity }),
  realSell: (id: number, quantity?: number) =>
    post<RealOrder>(`/portfolio/real/positions/${id}/sell`, quantity ? { quantity } : {}),
  trades: (limit = 100) => get<Trade[]>(`/portfolio/trades?limit=${limit}&book=bot`),
  testerSummary: () => get<PortfolioSummary>('/portfolio/summary?book=tester'),
  testerPositions: () => get<DetailedPosition[]>('/portfolio/positions/detailed?book=tester'),
  testerTrades: (limit = 500) => get<Trade[]>(`/portfolio/trades?limit=${limit}&book=tester&all_time=true`),
  testerPaperBuy: (body: { symbol: string; quantity: number; cap_tier: string; exchange?: string }) =>
    post<{ position_id: number; symbol: string; quantity: number; entry_price: number; cap_tier: string }>(
      '/portfolio/tester/buy',
      body,
    ),
  capitalAdded: () =>
    get<{ starting_capital: number; added: { at: string; amount: number }[]; total_added: number }>(
      '/portfolio/capital-added',
    ),
  addCapital: (amount: number, note?: string) =>
    post<{ added: number; at: string; account_value: number; cash: number }>('/portfolio/capital-added', {
      amount,
      note,
    }),
  equityCurve: (days = 180) => get<EquityPoint[]>(`/portfolio/equity-curve?days=${days}`),
  snapshot: () => post<MessageResponse>('/portfolio/snapshot'),

  // ------------------------------------------------------------ signals --
  latestSignals: (limit = 20) => get<LatestSignal[]>(`/signals/latest?limit=${limit}`),
  signalHistory: (symbol: string) =>
    get<LatestSignal[]>(`/signals/latest?symbol=${encodeURIComponent(symbol)}`),
  buyList: () => get<BuyListRow[]>('/signals/buy-list'),
  // Today's picks, already cut to what the signed-in person's plan shows.
  picks: () => get<PlanPick[]>('/signals/picks'),
  topPicks: (budget: number) => get<TopPick[]>(`/signals/top-picks?budget=${budget}`),
  scanResults: () => get<TrackRecordSignal[]>('/signals/track-record'),

  // --------------------------------------------------------- strategies --
  strategies: () => get<Strategy[]>('/strategies'),
  strategyTypes: () => get<StrategyType[]>('/strategies/types'),
  createStrategy: (body: Partial<Strategy>) => post<Strategy>('/strategies', body),
  updateStrategy: (id: number, body: Partial<Strategy>) =>
    patch<Strategy>(`/strategies/${id}`, body),
  deleteStrategy: (id: number) => del<MessageResponse>(`/strategies/${id}`),
  activateStrategy: (id: number) => post<Strategy>(`/strategies/${id}/activate`),
  deactivateStrategy: (id: number) => post<Strategy>(`/strategies/${id}/deactivate`),
  scanAll: () => post<ScanResult>('/strategies/scan-all'),
  strategyPerformance: () => get<StrategyPerformanceResponse>('/strategies/performance'),
  // Every signal type (including HOLD), symbol-resolved, newest first. A
  // strategy's most recent scan always sorts to the top `limit` rows, since
  // each scan writes a fresh batch strictly after the previous one.
  strategySignals: (id: number, limit = 100) =>
    get<StrategySignal[]>(`/strategies/${id}/signals?limit=${limit}`),

  // ---------------------------------------------------------------- ml --
  models: () => get<MLModel[]>('/ml/models'),
  activateModel: (id: number) => post<MLModel>(`/ml/models/${id}/activate`),
  train: (body: Record<string, unknown>) => post<MessageResponse>('/ml/train', body),
  predictionAccuracy: (modelId?: number) =>
    get<{ evaluated_predictions: number; correct: number; accuracy: number }>(
      modelId ? `/ml/predictions/accuracy?model_id=${modelId}` : '/ml/predictions/accuracy',
    ),
  // Scores the whole watchlist now, ranked by probability — the "what should
  // I focus on" view. persist=true also feeds the accuracy metric above.
  predict: (persist = true) => post<PredictionRun[]>(`/ml/predict?persist=${persist}`),
  // The history behind the accuracy stat: every prediction the bot has ever
  // made, symbol-resolved, with the outcome once its horizon has elapsed.
  stockScoreTrail: (symbol: string) => get<StockScoreTrail>(`/ml/score-trail/${encodeURIComponent(symbol)}`),
  predictions: (limit = 100) => get<Prediction[]>(`/ml/predictions?limit=${limit}`),
  // "What if we judged this model on X days instead?" — read-only re-score
  // of every prediction on record against an arbitrary horizon.
  predictionAccuracyAtHorizon: (horizonDays: number) =>
    get<HorizonAccuracy>(`/ml/predictions/accuracy/horizon?horizon_days=${horizonDays}`),

  // -------------------------------------------------------- instruments --
  watchlist: () => get<Instrument[]>('/instruments/watchlist'),
  setWatchlist: (symbols: string[]) => put<Instrument[]>('/instruments/watchlist', { symbols }),
  syncInstruments: () => post<MessageResponse>('/instruments/sync'),
  instruments: (search: string, limit = 8) =>
    get<Instrument[]>(`/instruments?search=${encodeURIComponent(search)}&limit=${limit}`),

  // ---------------------------------------------- my watchlist (per person) --
  // Each signed-in person's own list (any role). Not the bot's global watchlist above.
  myWatchlist: () => get<MyWatchlist>('/me/watchlist'),
  myWatchlistSearch: (q: string) => get<WatchlistHit[]>(`/me/watchlist/search?q=${encodeURIComponent(q)}`),
  myWatchlistAdd: (symbol: string, note?: string) => post<MyWatchlistItem>('/me/watchlist', { symbol, note }),
  myWatchlistRemove: (symbol: string) => del<MyWatchlistItem>(`/me/watchlist/${encodeURIComponent(symbol)}`),

  // -------------------------------------------------------- market data --
  candles: (symbol: string, limit = 11) =>
    get<Candle[]>(`/market-data/candles/${encodeURIComponent(symbol)}?interval=day&limit=${limit}`),
  shadowTest: () => get<ShadowTestView>('/brain/shadow-test'),
  company: (symbol: string) => get<CompanyView>(`/company/${encodeURIComponent(symbol)}`),
  compare: (symbols: string[]) => get<CompareView>(`/compare?symbols=${encodeURIComponent(symbols.join(','))}`),
  priceSnapshot: (symbol: string) =>
    get<PriceSnapshot>(`/market-data/snapshot/${encodeURIComponent(symbol)}`),
  backfill: (body: Record<string, unknown>) => post<MessageResponse>('/market-data/backfill', body),
  coverage: () =>
    get<{ symbol: string; candles: number; from: string | null; to: string | null }[]>(
      '/market-data/coverage',
    ),
  marketRegime: () => get<MarketRegime>('/market-data/regime'),
  sectors: (symbols: string[]) =>
    get<Record<string, string | null>>(`/market-data/sectors?symbols=${encodeURIComponent(symbols.join(','))}`),

  // ------------------------------------------------------ notifications --
  telegramStatus: () =>
    get<{ ok: boolean; bot: string | null; error: string | null }>('/notifications/telegram/status'),
  telegramTest: () => post<MessageResponse>('/notifications/telegram/test'),
  alertSettings: () => get<AlertSettings>('/notifications/alert-settings'),
  saveAlertSettings: (body: AlertSettingsUpdate) => put<AlertSettings>('/notifications/alert-settings', body),

  // --------------------------------------------------- real buy/sell report --
  realReport: (params: { start?: string; end?: string } = {}) =>
    get<RealReport>(`/real-report${qs(params)}`),
  syncRealTrades: () => post<MessageResponse>('/real-report/sync'),
  importTradebook: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return requestForm<MessageResponse>('/real-report/import-tradebook', form)
  },
  realTax: (year: string) => get<RealTaxExport>(`/real-report/tax${qs({ year })}`),
  realTaxCsv: (year: string) => requestText(`/real-report/tax.csv${qs({ year })}`),

  // ------------------------------------------------------------ finance --
  uploadFinanceStatement: (file: File, password?: string) => {
    const form = new FormData()
    form.append('file', file)
    if (password) form.append('password', password)
    return requestForm<FinanceIngestResult>('/finance/statements', form)
  },
  financeTransactions: (params: FinanceFilters & { search?: string } = {}) =>
    get<FinanceTransaction[]>(`/finance/transactions${qs(params)}`),
  recategorizeFinanceTransaction: (id: number, category: string) =>
    patch<FinanceTransaction>(`/finance/transactions/${id}`, { category }),
  updateFinanceTransaction: (
    id: number,
    changes: Partial<{ category: string; amount: number; description: string; txn_date: string }>,
  ) => patch<FinanceTransaction>(`/finance/transactions/${id}`, changes),
  deleteFinanceTransaction: (id: number) => del<MessageResponse>(`/finance/transactions/${id}`),
  financeCategories: () => get<string[]>('/finance/categories'),
  // Always spans every month by design — see the backend endpoint's own
  // docstring. Still respects category/direction.
  financeMonthlySummary: (filters: Omit<FinanceFilters, 'month'> = {}) =>
    get<FinanceMonthlySummary[]>(`/finance/summary/monthly${qs(filters)}`),
  financeCategorySummary: (filters: Omit<FinanceFilters, 'category'> = {}) =>
    get<FinanceCategorySummary[]>(`/finance/summary/categories${qs(filters)}`),
  financeMonthlyCategorySummary: () =>
    get<FinanceMonthlyCategorySummary[]>('/finance/summary/monthly-categories'),
  financeMerchants: (filters: FinanceFilters = {}, topN = 20) =>
    get<FinanceMerchantSummary[]>(`/finance/merchants${qs({ ...filters, top_n: topN })}`),
  financeInsights: (filters: FinanceFilters = {}) => get<string[]>(`/finance/insights${qs(filters)}`),
  financeCalculation: (filters: FinanceFilters = {}) =>
    get<FinanceCalculationSummary>(`/finance/calculation${qs(filters)}`),
  financeNetWorth: (filters: FinanceFilters = {}) => get<FinanceNetWorth>(`/finance/net-worth${qs(filters)}`),
  financeStatements: () => get<FinanceIngestedFile[]>('/finance/statements'),
  financeDeletedStatements: () => get<FinanceIngestedFile[]>('/finance/statements?deleted=true'),
  // Soft delete — reversible via restoreFinanceStatement, so no confirmation
  // dialog is needed at the call site.
  deleteFinanceStatement: (id: number) => del<MessageResponse>(`/finance/statements/${id}`),
  restoreFinanceStatement: (id: number) => post<FinanceIngestedFile>(`/finance/statements/${id}/restore`),
  permanentlyDeleteFinanceStatement: (id: number, filename: string) =>
    del<MessageResponse>(`/finance/statements/${id}/permanent?confirm_filename=${encodeURIComponent(filename)}`),

  // -------------------------------------------------------- finance loans --
  financeLoans: () => get<FinanceLoan[]>('/finance/loans'),
  createFinanceLoan: (body: Record<string, unknown>) => post<FinanceLoan>('/finance/loans', body),
  updateFinanceLoan: (id: number, body: Record<string, unknown>) =>
    patch<FinanceLoan>(`/finance/loans/${id}`, body),
  deleteFinanceLoan: (id: number) => del<MessageResponse>(`/finance/loans/${id}`),

  // -------------------------------------------------------- mutual funds --
  mutualFundSearch: (q: string) => get<MutualFundSearchResult[]>(`/mutual-funds/search?q=${encodeURIComponent(q)}`),
  mutualFundHoldings: () => get<MutualFundHolding[]>('/mutual-funds/holdings'),
  createMutualFundHolding: (payload: {
    scheme_id: number
    units: number
    purchase_nav: number
    purchase_date: string
    notes?: string
  }) => post<MutualFundHolding>('/mutual-funds/holdings', payload),
  deleteMutualFundHolding: (id: number) => del<MessageResponse>(`/mutual-funds/holdings/${id}`),

  // ------------------------------------------------------- recurring bills --
  recurringBills: (month?: string) =>
    get<FinanceRecurringBill[]>(`/finance/recurring-bills${month ? `?month=${month}` : ''}`),
  createRecurringBill: (body: Record<string, unknown>) =>
    post<FinanceRecurringBill>('/finance/recurring-bills', body),
  addDefaultRecurringBills: () =>
    post<FinanceRecurringBill[]>('/finance/recurring-bills/defaults', {}),
  updateRecurringBill: (id: number, body: Record<string, unknown>) =>
    patch<FinanceRecurringBill>(`/finance/recurring-bills/${id}`, body),
  deleteRecurringBill: (id: number) => del<MessageResponse>(`/finance/recurring-bills/${id}`),
  payRecurringBill: (id: number, body: { month: string; amount: number; paid_at?: string }) =>
    post<FinanceRecurringBill>(`/finance/recurring-bills/${id}/pay`, body),
  unpayRecurringBill: (id: number, month: string) =>
    del<MessageResponse>(`/finance/recurring-bills/${id}/pay/${month}`),

  // ----------------------------------------------------------- daily spend --
  dailyCategories: () => get<FinanceDailyCategory[]>('/finance/daily-categories'),
  createDailyCategory: (name: string) =>
    post<FinanceDailyCategory>('/finance/daily-categories', { name }),
  deleteDailyCategory: (id: number) => del<MessageResponse>(`/finance/daily-categories/${id}`),
  createDailyExpense: (body: { category: string; amount: number; spent_at?: string; note?: string }) =>
    post<FinanceTransaction>('/finance/daily-expenses', body),

  // -------------------------------------------------------- finance rules --
  financeRules: () => get<FinanceCustomRule[]>('/finance/rules'),
  upsertFinanceRule: (keyword: string, category: string, priority = 90) =>
    put<FinanceRuleUpsertResult>('/finance/rules', { keyword, category, priority }),
  deleteFinanceRule: (keyword: string) =>
    del<MessageResponse>(`/finance/rules/${encodeURIComponent(keyword)}`),
  recategorizeFinanceRules: () => post<FinanceRecategorizeResult>('/finance/rules/recategorize'),

  // ----------------------------------------------------------- risk layer --
  riskLimits: () => get<CurrentLimits>('/risk/limits'),
  safetyState: () => get<SystemState>('/safety/state'),
  haltEntries: (reason: string) => post<SystemState>('/safety/halt', { reason }),
  resumeEntries: () => post<SystemState>('/safety/resume', {}),
  riskEvents: (limit = 50) => get<RiskEvent[]>(`/safety/risk-events?limit=${limit}`),

  // ----------------------------------------------------------- calibration --
  calibration: (source: 'signals' | 'predictions' = 'signals') =>
    get<CalibrationReport>(`/ml/calibration?source=${source}`),

  // ---------------------------------------------------------------- brain --
  brainModules: () => get<BrainModules>('/brain/modules'),
  setBrainModuleMode: (id: string, mode: 'on' | 'shadow' | 'off') =>
    put<{ module_id: string; mode: string }>(`/brain/modules/${id}`, { mode }),
  brainHealth: () => get<BrainHealth>('/brain/health'),
  // ux11: today's nightly ideas next to the previous trading day's (read only).
  brainChanges: (runId: string) => get<BrainChanges>(`/brain/changes?run_id=${encodeURIComponent(runId)}`),
  brainEpisodes: () => get<BrainEpisode[]>('/brain/episodes'),
  brainTrack: (symbol: string) => get<BrainTrack>(`/brain/track/${encodeURIComponent(symbol)}`),
  brainWhatIf: (symbol: string, qty: number, price?: number) =>
    post<BrainWhatIf>('/brain/whatif', { symbol, qty, price }),
  marketSession: () => get<MarketSession>('/market/session'),
  brainRuns: (limit = 10) => get<BrainRunSummary[]>(`/brain/runs?limit=${limit}`),
  brainLatestRun: (kind = 'nightly') => get<BrainRun>(`/brain/runs/latest?kind=${kind}`),
  // Queues a run and answers at once; poll brainRunGet until done/failed.
  brainRunNow: () => post<BrainRunQueued>('/brain/runs', { kind: 'nightly' }),
  brainRunGet: (runId: string) => get<BrainRun>(`/brain/runs/${encodeURIComponent(runId)}`),
  brainRunTrace: (runId: string) =>
    get<{ run_id: string; trace: BrainTraceEvent[] }>(`/brain/runs/${encodeURIComponent(runId)}/trace`),
  brainWhy: (symbol: string) => get<BrainWhy>(`/brain/why/${encodeURIComponent(symbol.trim().toUpperCase())}`),
  // What the brain really sent, newest first (the top-bar bell). Read only; owner's area.
  brainAlerts: (limit = 20) => get<BrainAlertRecord[]>(`/brain/alerts?limit=${limit}`),
  brainAlertPreview: (runId: string) =>
    get<BrainAlertPreview>(`/brain/runs/${encodeURIComponent(runId)}/alerts`),
  brainAlertSend: (runId: string) =>
    post<BrainAlertSend>(`/brain/runs/${encodeURIComponent(runId)}/alerts/send`),
  brainOverrule: (id: number, word: string, reason: string) =>
    post<BrainDecision>(`/brain/decisions/${id}/overrule`, { word, reason }),
  // M09 learning loop: a read-only report plus proposals the owner can
  // accept or reject — accepting is the only thing that ever changes
  // behaviour (constitution C9).
  brainLearning: (since?: string) => get<BrainLearning>(`/brain/learning${since ? `?since=${since}` : ''}`),
  brainProposals: () => get<BrainProposal[]>('/brain/proposals'),
  brainProposalDecide: (id: number, action: 'accept' | 'reject', note: string) =>
    post<BrainProposal>(`/brain/proposals/${id}/${action}`, { note }),
  brainRevertBuyLevel: () => post<BrainProposal>('/brain/proposals/buy-level/revert', {}),

  // M18 go-live: comparison, the owner's stage, approvals.
  brainCompare: () => get<BrainCompare>('/brain/compare'),
  brainStage: () => get<BrainStage>('/brain/stage'),
  brainSetStage: (stage: BrainStageName, reason: string) => put<BrainStage>('/brain/stage', { stage, reason }),
  brainApprovals: () => get<BrainApproval[]>('/brain/approvals'),
  brainApprove: (id: number, note: string) => post<BrainApproval>(`/brain/approvals/${id}/approve`, { note }),
  brainReject: (id: number, reason: string) => post<BrainApproval>(`/brain/approvals/${id}/reject`, { reason }),

  // ---------------------------------------------- brain observatory (owner, read-only) --
  obsDays: (from: string, to: string) => get<ObsDays>(`/brain/observatory/days?from=${from}&to=${to}`),
  // Horizon experiments (research ledger, owner-only): owner-selected 5- and 30-day picks, simulated.
  experimentsReport: () => get<ExperimentReport>('/experiments/report'),
  experimentsOutcomes: () => get<ExperimentOutcome[]>('/experiments/outcomes'),
  experimentsSelections: () => get<ExperimentSelection[]>('/experiments/selections'),
  experimentAddPick: (pick: ExperimentPickIn) => post<ExperimentSelection>('/experiments/selections', pick),
  experimentCorrectPick: (id: number, pick: ExperimentPickIn) =>
    post<ExperimentSelection>(`/experiments/selections/${id}/correct`, pick),
  obsLearning: (from: string, to: string) => get<ObsLearning>(`/brain/observatory/learning?from=${from}&to=${to}`),
  obsForwardGate: () => get<ObsForwardGate>('/brain/observatory/forward-gate'),
  obsRunsPage: (date: string, afterStartedAt?: string, afterId?: string) =>
    get<ObsRuns>(
      `/brain/observatory/runs?date=${date}` +
        (afterStartedAt && afterId
          ? `&after_started_at=${encodeURIComponent(afterStartedAt)}&after_id=${encodeURIComponent(afterId)}`
          : ''),
    ),
  obsRun: (runId: string) => get<ObsRunDetail>(`/brain/observatory/runs/${encodeURIComponent(runId)}`),
  obsStocks: (runId: string, symbol?: string, afterId?: number) =>
    get<ObsStocks>(
      `/brain/observatory/runs/${encodeURIComponent(runId)}/stocks?limit=500` +
        (symbol ? `&symbol=${encodeURIComponent(symbol.trim().toUpperCase())}` : '') +
        (afterId !== undefined ? `&after_id=${afterId}` : ''),
    ),
  obsStock: (runId: string, decisionId: number) =>
    get<ObsStockDetail>(`/brain/observatory/runs/${encodeURIComponent(runId)}/stocks/${decisionId}`),
  obsOutcomes: (from: string, to: string, category: 'practice' | 'backtest') =>
    get<ObsOutcomes>(`/brain/observatory/outcomes?from=${from}&to=${to}&category=${category}`),
  obsEffectiveness: () => get<{ claims: ObsClaim[] }>('/brain/observatory/effectiveness'),
  obsScoreBands: (from: string, to: string) =>
    get<ObsScoreBands>(`/brain/observatory/score-bands?from=${from}&to=${to}`),
}
